import type { GameState, Problem, Rock, Shot, Viewport } from "./types";
import {
  ANS_MAX,
  ANS_MIN,
  CAND_SLOW,
  CAND_SWAY,
  FREEZE_MS,
  INTERMISSION_MS,
  KILLS_PER_WAVE,
  MAX_CHARGES,
  SHIP_MAX_ANGLE,
  SHOT_SPEED,
  SHOT_STAGGER_MS,
  SPAWN_Y,
  STAGE_FALL,
  START_LIVES,
  WAVES_PER_CHARGE,
  mutChance,
  triChance,
  waveParams,
} from "./constants";
import { band, pickBand } from "./bands";
import { LINE, SHIP_Y, VP_X, depthOf, rockRadius, scaleOf, screenX } from "./geometry";
import { random, ri, seed } from "./rng";
import { sceneFor } from "./scene";

/**
 * Nucleo del juego. Este archivo no toca el DOM, no dibuja y no reproduce
 * sonido: emite eventos en state.events y quien los quiera los consume.
 *
 * Esa separacion es lo que permitiria correr la simulacion en el servidor para
 * verificar puntajes por replay, sin reescribir nada.
 */

let reduceMotion = false;
export function setReduceMotion(v: boolean): void {
  reduceMotion = v;
}

export function createState(startWave = 1, runSeed = Date.now()): GameState {
  seed(runSeed);
  const wave = Math.max(1, startWave);
  return {
    wave,
    score: 0,
    lives: START_LIVES,
    buffer: "",
    rocks: [],
    shots: [],
    debris: [],
    spawnAt: 0,
    kills: 0,
    over: false,
    shake: 0,
    flash: 0,
    err: 0,
    inter: 1500,
    interWave: wave,
    hardestWave: 1,
    hardestRun: 1,
    charges: Math.min(MAX_CHARGES, 1 + Math.floor((wave - 1) / WAVES_PER_CHARGE)),
    used: 0,
    freeze: 0,
    shipA: 0,
    shipTA: 0,
    scene: sceneFor(wave),
    prevScene: null,
    fade: 1,
    drift: 0,
    keystrokes: 0,
    startedAt: 0,
    endedAt: 0,
    events: [],
    tempo: 1,
    depth: 1,
    startWave: wave,
  };
}

const isCandidateFor = (s: GameState, r: Rock): boolean =>
  s.buffer.length > 0 && String(r.ans).startsWith(s.buffer);

/**
 * Prefijo libre en ambos sentidos: ninguna respuesta viva puede ser prefijo de
 * otra. Es lo que permite respuestas de 2 a 4 digitos sin tecla de confirmar,
 * porque una coincidencia exacta nunca es ambigua.
 */
function conflicts(s: GameState, ans: number, self?: Rock): boolean {
  const a = String(ans);
  return s.rocks.some((r) => {
    if (!r.alive || r === self) return false;
    const o = String(r.ans);
    return a === o || a.startsWith(o) || o.startsWith(a);
  });
}

export function makeProblem(s: GameState, n: number, self?: Rock): Problem | null {
  const B = band(n);
  for (let k = 0; k < 220; k++) {
    const p = B.gen();
    if (!p) continue;
    if (!Number.isInteger(p.ans) || p.ans < ANS_MIN || p.ans > ANS_MAX) continue;
    if (p.txt.includes(".")) continue;
    if (conflicts(s, p.ans, self)) continue;
    return { txt: p.txt, ans: p.ans, band: B.n, fall: B.fall };
  }
  return null;
}

const rockVerts = (): number[] => {
  const v: number[] = [];
  for (let i = 0; i < 12; i++) v.push(0.76 + random() * 0.4);
  return v;
};

export function spawn(s: GameState, vp: Viewport): void {
  const P = waveParams(s.wave);
  let bn = pickBand(s.wave);
  let p = makeProblem(s, bn);
  for (let t = 0; !p && t < 20; t++) {
    bn = Math.max(1, bn - 1);
    p = makeProblem(s, bn);
  }
  if (!p) return;

  let stages = 1;
  if (random() < mutChance(s.wave)) stages = random() < triChance(s.wave) ? 3 : 2;

  const seconds = (P.descent * p.fall * STAGE_FALL[stages]!) / s.tempo;

  s.rocks.push({
    x: VP_X(vp),
    y: SPAWN_Y,
    vy: LINE(vp) / (seconds * 1000),
    lane: (random() * 2 - 1) * 0.95,
    swayAmp: 14 + random() * 18,
    swayFreq: 0.0007 + random() * 0.0009,
    swayPhase: random() * 6.28,
    txt: p.txt,
    ans: p.ans,
    band: p.band,
    stages,
    verts: rockVerts(),
    rot: random() * 6.28,
    rotV: (random() - 0.5) * 0.0007,
    alive: true,
    incoming: 0,
    jolt: 0,
    chip: 0,
  });

  s.hardestWave = Math.max(s.hardestWave, p.band);
  s.hardestRun = Math.max(s.hardestRun, p.band);
}

/** Rocas apuntables: vivas y sin disparos en vuelo. */
const candidates = (s: GameState): Rock[] =>
  s.rocks.filter((r) => r.alive && r.incoming === 0 && String(r.ans).startsWith(s.buffer));

export function press(s: GameState, digit: string, vp: Viewport): void {
  if (s.over) return;
  s.keystrokes++;
  s.events.push({ kind: "key", digit: Number(digit) });
  s.buffer += digit;

  const c = candidates(s);
  if (c.length === 0) {
    s.buffer = "";
    s.err = 1;
    s.events.push({ kind: "error" });
    return;
  }
  const exact = c
    .filter((r) => String(r.ans) === s.buffer)
    .sort((a, b) => b.y - a.y)[0];
  if (exact) {
    s.buffer = "";
    fire(s, exact, vp);
  }
}

export function clearBuffer(s: GameState): void {
  s.buffer = "";
}

/** Un disparo por digito de la respuesta. La roca solo se rompe con el ultimo. */
function fire(s: GameState, r: Rock, _vp: Viewport): void {
  const n = String(r.ans).length;
  r.incoming = n;
  for (let i = 0; i < n; i++) {
    s.shots.push({
      target: r,
      delay: i * SHOT_STAGGER_MS,
      idx: i,
      x: 0,
      y: 0,
      dx: 0,
      dy: -1,
      started: false,
      speed: SHOT_SPEED,
      dead: false,
    });
  }
}

function debris(s: GameState, x: number, y: number, n: number, r: number): void {
  for (let i = 0; i < n; i++) {
    const a = random() * 6.283;
    const sp = 0.6 + random() * 2.0;
    s.debris.push({
      x: x + Math.cos(a) * r * 0.5,
      y: y + Math.sin(a) * r * 0.5,
      vx: Math.cos(a) * sp,
      vy: Math.sin(a) * sp,
      life: 1,
      len: 3 + random() * 8,
      rot: random() * 6.28,
    });
  }
}

function nextWave(s: GameState): void {
  s.kills = 0;
  s.wave++;
  s.hardestWave = 1;
  s.inter = INTERMISSION_MS;
  s.interWave = s.wave;
  s.prevScene = s.scene;
  s.scene = sceneFor(s.wave);
  s.fade = 0;
  const earned = 1 + Math.floor((s.wave - 1) / WAVES_PER_CHARGE);
  s.charges = Math.min(MAX_CHARGES, earned - s.used);
  s.events.push({ kind: "warp" });
}

/** Salta de ola sin limpiarla. Solo para inspeccionar escenografia. */
export function skipWave(s: GameState): void {
  if (!s.over) nextWave(s);
}

function impact(s: GameState, sh: Shot): void {
  const r = sh.target;
  if (!r.alive) return;

  r.incoming--;
  r.jolt = 1;

  if (r.incoming > 0) {
    debris(s, sh.x, sh.y, 3, 4);
    s.events.push({ kind: "tick" });
    return;
  }

  if (r.stages > 1) {
    // fragmentar: misma roca, problema mas duro, una etapa menos
    r.stages--;
    s.score += 50 + r.band * 12;
    const nb = r.band + 1 + ri(2);
    const keep = { txt: r.txt, ans: r.ans, band: r.band };
    const p = makeProblem(s, nb, r) ?? makeProblem(s, r.band + 1, r);
    if (p) {
      r.txt = p.txt;
      r.ans = p.ans;
      r.band = p.band;
      s.hardestWave = Math.max(s.hardestWave, p.band);
      s.hardestRun = Math.max(s.hardestRun, p.band);
    } else {
      r.txt = keep.txt;
      r.ans = keep.ans;
      r.band = keep.band;
    }
    r.verts = rockVerts();
    r.chip = 420;
    debris(s, r.x, r.y, 9, rockRadius(r.band, r.stages, 1) * 0.5);
    s.events.push({ kind: "chip", left: r.stages });
    s.events.push({ kind: "boom", big: false });
    return;
  }

  r.alive = false;
  s.score += 100 + r.band * 20;
  s.kills++;
  debris(s, r.x, r.y, 18, rockRadius(r.band, 1, 1) * 0.8);
  s.events.push({ kind: "resolve" });
  s.events.push({ kind: "boom", big: true });
  if (s.kills >= KILLS_PER_WAVE) nextWave(s);
}

function breach(s: GameState, r: Rock, now: number): void {
  r.alive = false;
  s.lives--;
  s.flash = 1;
  if (!reduceMotion) s.shake = 15;
  s.events.push({ kind: "breach" });
  if (s.lives <= 0) {
    s.over = true;
    s.endedAt = now;
  }
}

export function useFreeze(s: GameState): boolean {
  if (s.over || s.freeze > 0 || s.charges <= 0) return false;
  s.charges--;
  s.used++;
  s.freeze = FREEZE_MS;
  s.events.push({ kind: "freezeOn" });
  return true;
}

/**
 * Un paso de simulacion. `now` y `dt` en milisegundos.
 * Los disparos siguen viajando durante el congelamiento: una solucion ya hecha
 * tiene que resolverse.
 */
export function step(s: GameState, now: number, dt: number, vp: Viewport): void {
  const P = waveParams(s.wave);
  const frozen = s.freeze > 0;

  if (!frozen) s.drift += dt * 0.05;
  if (s.fade < 1) s.fade = Math.min(1, s.fade + dt / 900);

  if (frozen) {
    s.freeze -= dt;
    s.spawnAt += dt;
    if (s.freeze <= 0) {
      s.freeze = 0;
      s.events.push({ kind: "freezeOff" });
    }
  }
  if (s.inter > 0) {
    s.inter -= dt;
    s.spawnAt += dt;
  }

  if (!s.over) {
    const alive = s.rocks.filter((r) => r.alive).length;
    if (!frozen && s.inter <= 0 && now > s.spawnAt && alive < P.maxAlive) {
      s.spawnAt = now + (P.spawn * 1000) / s.tempo;
      spawn(s, vp);
    }

    for (const r of s.rocks) {
      if (!r.alive) continue;
      const cand = isCandidateFor(s, r);
      if (!frozen) {
        r.y += r.vy * dt * (cand ? CAND_SLOW : 1);
        r.rot += r.rotV * dt;
      }
      r.x = screenX(r, now, vp, s.depth, cand, CAND_SWAY, reduceMotion);
      if (r.jolt > 0) r.jolt -= dt / 180;
      if (r.chip > 0) r.chip -= dt;
      if (r.y > LINE(vp)) breach(s, r, now);
    }
    s.rocks = s.rocks.filter((r) => r.alive);
  }

  for (const sh of s.shots) {
    const t = sh.target;
    if (!t.alive) {
      sh.dead = true;
      continue;
    }
    if (!sh.started) {
      if (sh.delay > 0) {
        sh.delay -= dt;
        continue;
      }
      sh.started = true;
      sh.x = VP_X(vp) + Math.sin(s.shipA) * 18;
      sh.y = SHIP_Y(vp) - Math.cos(s.shipA) * 18;
      s.events.push({ kind: "fire", idx: sh.idx });
    }
    const ddx = t.x - sh.x;
    const ddy = t.y - sh.y;
    const d = Math.hypot(ddx, ddy) || 1;
    sh.dx = ddx / d;
    sh.dy = ddy / d;
    const stepLen = sh.speed * dt;
    const hitR = rockRadius(t.band, t.stages, scaleOf(depthOf(t.y, vp), s.depth)) * 0.75;
    if (d <= stepLen + hitR) {
      impact(s, sh);
      sh.dead = true;
    } else {
      sh.x += sh.dx * stepLen;
      sh.y += sh.dy * stepLen;
    }
  }
  s.shots = s.shots.filter((sh) => !sh.dead);

  // la nave esta fija al centro y solo rota sobre su propio eje
  const aim = s.shots.find((sh) => sh.target.alive);
  if (aim) {
    const t = aim.target;
    const a = Math.atan2(t.x - VP_X(vp), Math.max(40, SHIP_Y(vp) - t.y));
    s.shipTA = Math.max(-SHIP_MAX_ANGLE, Math.min(SHIP_MAX_ANGLE, a));
  } else {
    s.shipTA = 0;
  }
  s.shipA += (s.shipTA - s.shipA) * Math.min(1, dt / 130);

  for (const d of s.debris) {
    d.x += d.vx * dt * 0.06;
    d.y += d.vy * dt * 0.06;
    d.rot += dt * 0.004;
    d.life -= dt / 700;
  }
  s.debris = s.debris.filter((d) => d.life > 0);

  s.shake = Math.max(0, s.shake - dt / 34);
  s.flash = Math.max(0, s.flash - dt / 460);
  s.err = Math.max(0, s.err - dt / 300);
}

/** Profundidad de la roca mas cercana a la linea, 0..1. Para el aviso de peligro. */
export function nearestThreat(s: GameState, vp: Viewport): number {
  return s.rocks.reduce((m, r) => (r.alive ? Math.max(m, depthOf(r.y, vp)) : m), 0);
}
